import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import PaymentValidationModal from './components/PaymentValidationModal';
import ServiceChargeModal from './components/ServiceChargeModal';
import ProfileUpgradeModal from './components/ProfileUpgradeModal';
import ProcessingModal from './components/ProcessingModal';
import EmailReceiptModal from './components/EmailReceiptModal';
import APIRetryModal from './components/APIRetryModal';
import ServiceHeader from './components/ServiceHeader';
import TransactionHistoryModal from './components/TransactionHistoryModal';
import ReadAloudButton from './components/ReadAloudButton';
import SegmentedReadAloud from './components/SegmentedReadAloud';
import PaymentFlow from './services/paymentFlow';
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Badge } from "./components/ui/badge";
import { toast } from "sonner";
import { Loader2, BookOpen, ChevronLeft, FileText, Download, CheckCircle, AlertCircle } from "lucide-react";
import { convertToUserTimezone } from './utils/timezoneHandler';
import { useTimezone } from './contexts/TimezoneContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ERMTPage = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { formatTimestamp } = useTimezone();
  
  // Check where user came from
  const fromLocation = searchParams.get('from');
  const returnPath = fromLocation === 'activity' ? '/user_dashboard' : '/homepage';
  const returnLabel = fromLocation === 'activity' ? 'Return to Dashboard' : 'Return to Home';
  
  // Amazon KDP Genre categories for manuscript analysis
  const genres = [
    { 
      value: "fiction", 
      label: "Fiction", 
      subGenres: [
        "Action & Adventure", "African American Fiction", "Anthologies", "Classics", 
        "Coming of Age", "Comics & Graphic Novels", "Contemporary Fiction", "Crime Fiction", 
        "Drama & Plays", "Erotica", "Fairy Tales, Folk Tales & Myths", "Fantasy", 
        "Historical Fiction", "Horror", "Humor & Satire", "Literary Fiction", 
        "Mystery, Thriller & Suspense", "Romance", "Science Fiction", "Short Stories", 
        "Teen & Young Adult Fiction", "Westerns", "Women's Fiction"
      ] 
    },
    { 
      value: "non-fiction", 
      label: "Non-Fiction", 
      subGenres: [
        "Arts & Photography", "Biographies & Memoirs", "Business & Money", 
        "Children's Nonfiction", "Comics & Graphic Novels (Nonfiction)", 
        "Computers & Technology", "Cookbooks, Food & Wine", "Crafts, Hobbies & Home",
        "Education & Teaching", "Engineering & Transportation", "Health, Fitness & Dieting",
        "History", "Humor & Entertainment", "Law", "LGBTQ+ Books", 
        "Literature & Fiction (Guides)", "Medical Books", "Parenting & Relationships",
        "Politics & Social Sciences", "Reference", "Religion & Spirituality",
        "Science & Math", "Self-Help", "Sports & Outdoors", 
        "Teen & Young Adult Nonfiction", "Travel"
      ] 
    },
    { 
      value: "poetry", 
      label: "Poetry", 
      subGenres: ["Contemporary Poetry", "Classic Poetry", "Spoken Word", "Haiku", "Free Verse"] 
    },
    { 
      value: "children", 
      label: "Children's Books", 
      subGenres: ["Picture Books", "Early Readers", "Middle Grade", "Young Adult", "Educational"] 
    },
    { 
      value: "academic", 
      label: "Academic/Educational", 
      subGenres: ["Textbook", "Research", "Technical Manual", "Reference", "Educational Guide", "Professional Development", "Training Materials"] 
    }
  ];

  const [formData, setFormData] = useState({
    title: "",
    subtitle: "",
    author_name: "",
    publisher: "",
    genre: "",
    sub_genre: ""
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFrontMatter, setSelectedFrontMatter] = useState(null);
  const [selectedCoverImage, setSelectedCoverImage] = useState(null);
  const fileRef = useRef(null); // Persist file reference
  const frontMatterRef = useRef(null); // Persist front matter reference
  const coverImageRef = useRef(null); // Persist cover image reference
  const formDataRef = useRef(null); // Persist form data reference
  
  // API Retry Modal State
  const [showRetryModal, setShowRetryModal] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryError, setRetryError] = useState('');
  const maxRetryAttempts = 3;
  
  // Manual retry handler
  const handleManualRetry = async () => {
    setRetryAttempt(prev => prev + 1);
    setIsRetrying(true);
    setShowRetryModal(false); // Close modal while retrying
    
    try {
      // Trigger form submission again (form data is preserved in state)
      // Create a synthetic event to pass to handleSubmit
      const syntheticEvent = { preventDefault: () => {} };
      await handleSubmit(syntheticEvent);
      // If successful, reset retry state
      setRetryAttempt(0);
      setRetryError('');
    } catch (error) {
      // If still failing, show modal again
      console.error('❌ Manual retry failed:', error);
      setShowRetryModal(true);
    } finally {
      setIsRetrying(false);
    }
  };
  
  // Cancel retry
  const handleCancelRetry = () => {
    setShowRetryModal(false);
    setRetryAttempt(0);
    setRetryError('');
    toast.info('Analysis cancelled. Your form data is saved - you can try again anytime.');
  };
  const [pricingInfo, setPricingInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [userHasPaymentMethod, setUserHasPaymentMethod] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [progressSteps, setProgressSteps] = useState([
    { text: 'Uploading manuscript', completed: false, current: false },
    { text: 'Converting to EPUB', completed: false, current: false },
    { text: 'Generating final file', completed: false, current: false }
  ]);
  const [showPaymentValidation, setShowPaymentValidation] = useState(false);
  const [showChargeModal, setShowChargeModal] = useState(false);
  const [chargeInfo, setChargeInfo] = useState(null);
  const [serviceAccessDenied, setServiceAccessDenied] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [reports, setReports] = useState([]);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showEmailReceiptModal, setShowEmailReceiptModal] = useState(false);
  const [lastTransactionId, setLastTransactionId] = useState(null);
  const [lastCharge, setLastCharge] = useState(null);

  // UI State
  const [usageStats, setUsageStats] = useState({ total: 0, monthly: 0 });
  const [showTransactionHistory, setShowTransactionHistory] = useState(false);
  const [transactions, setTransactions] = useState([]);

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    loadUsageStats(); // Load usage statistics on mount
    loadTransactions(); // Load transactions for overview
  }, []);
  
  const loadTransactions = async () => {
    try {
      const response = await axios.get(`${API}/transactions`, {
        withCredentials: true
      });
      const ermtTransactions = (response.data || []).filter(t => 
        t.service && t.service.toUpperCase() === 'ERMT'
      );
      setTransactions(ermtTransactions);
    } catch (error) {
      console.error("Error loading transactions:", error);
    }
  };

  useEffect(() => {
    loadUserReports();
    
    // Check if we need to load a specific report from URL parameter
    const reportId = searchParams.get('report') || searchParams.get('viewReport');
    
    if (reportId) {
      // Loading specific report - allow access
      loadAndDisplayReport(reportId);
      setPaymentCompleted(true); // Skip payment validation for viewing reports
    } else {
      // NORMAL SERVICE ACCESS - VALIDATE PAYMENT FIRST
      validateServiceAccess();
    }

    // Listen for payment success messages from popup window
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'PAYMENT_SUCCESS') {
        toast.success("Payment successful! You can now start the analysis.");
        
        // Set payment completed and stop processing
        setPaymentCompleted(true);
        setPaymentProcessing(false);
        
        // Verify payment status but don't auto-start analysis
        verifyPaymentStatus(event.data.sessionId);
      }
    };

    window.addEventListener('message', handleMessage);
    
    // Cleanup
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [location]);

  const verifyPaymentStatus = async (sessionId) => {
    
    try {
      const statusResponse = await axios.get(
        `${API}/payments/checkout/status/${sessionId}`, 
        { withCredentials: true }
      );
      
      
      if (statusResponse.data.payment_status === 'paid') {
        setPaymentCompleted(true);
        setPaymentProcessing(false);
        
        // Clear URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
        
      } else {
        setPaymentProcessing(false);
      }
      
    } catch (error) {
      console.error('🔥 Payment verification error:', error);
      setPaymentProcessing(false);
    }
  };

  const validateServiceAccess = async () => {
    try {
      console.log('🔍 ERMT: Starting service access validation...');
      
      const verification = await PaymentFlow.verifyPaymentForService('ermt');
      console.log('✅ ERMT: Backend verification response:', verification);
      
      if (verification.access_granted) {
        console.log('✅ ERMT: Access granted! Setting paymentCompleted to true');
        
        // Store discount info if provided (for subscribers)
        if (verification.discount_info) {
          setChargeInfo(verification.discount_info);
        }
        
        setPaymentCompleted(true);
        console.log('✅ ERMT: Payment validation complete - user can access service');
        return;
      }
      
      if (verification.requires_payment_validation) {
        console.log('❌ ERMT: Payment validation required - showing modal');
        setShowPaymentValidation(true);
        setServiceAccessDenied(true);
        return;
      }
      
      // Fallback - deny access
      console.log('❌ ERMT: Fallback - denying access');
      setServiceAccessDenied(true);
      
    } catch (error) {
      console.error('🔥 ERMT: Service validation error:', error);
      console.error('🔥 ERMT: Error response:', error.response?.data);
      setServiceAccessDenied(true);
    }
  };

  const handlePaymentValidationSuccess = (result, redirectLocation) => {
    const roleName = result.user_role ? result.user_role.replace('_', ' ') : 'A la Carte';
    toast.success(`Payment method added! You are now an ${roleName} user.`);
    
    // Close modal and grant access to service
    setShowPaymentValidation(false);
    setServiceAccessDenied(false);
    setPaymentCompleted(true);
    
    // If they came from activities, we stay on service page
    // If they were redirected, we could navigate back, but since we're staying on ERMT, just proceed
  };

  const handleServiceChargeAuthorization = async () => {
    try {
      // Close modal and proceed with service (payment handled by backend)
      setShowChargeModal(false);
      
      // Proceed with actual ERMT analysis
      await submitForAnalysis();
    } catch (error) {
      console.error('🔥 Service charge authorization error:', error);
      const message = error.response?.data?.detail || error.message || 'Charge authorization failed';
      toast.error(message);
    }
  };

  const handleProfileUpgrade = async (upgradeType) => {
    try {
      
      // Call profile upgrade endpoint
      const response = await axios.post(`${API}/profile/upgrade`, {
        upgrade_type: upgradeType,
        service_context: 'ermt'
      }, { withCredentials: true });
      
      if (response.data.success) {
        toast.success(`Profile upgraded to ${upgradeType.replace('_', ' ')} successfully!`);
        
        // Close modal and grant access
        setShowUpgradeModal(false);
        setPaymentCompleted(true);
        
        // Show confirmation message
        toast.success(`Welcome to your new ${upgradeType.replace('_', ' ')} account! You now have access to ERMT service.`);
      } else {
        throw new Error(response.data.message || 'Upgrade failed');
      }
    } catch (error) {
      console.error('🔥 Profile upgrade error:', error);
      const message = error.response?.data?.detail || error.message || 'Profile upgrade failed';
      toast.error(message);
    }
  };

  const checkUserPaymentStatus = async () => {
    try {
      // Get fresh user data to check payment method
      const userResponse = await axios.get(`${API}/debug/user-profile`, { withCredentials: true });
      const userData = userResponse.data;
      
      
      const hasPaymentMethod = userData.has_payment_method;
      const isPayingRole = ['admin', 'student'].includes(userData.role) || hasPaymentMethod;
      
      setUserHasPaymentMethod(hasPaymentMethod);
      setPaymentCompleted(isPayingRole);
      
      
      return { hasPaymentMethod, isPayingRole, role: userData.role };
      
    } catch (error) {
      console.error('Error checking user payment status:', error);
      return { hasPaymentMethod: false, isPayingRole: false, role: 'free_user' };
    }
  };

  const checkPricing = async (showModal = true) => {
    try {
      // First, always check the user's actual payment status
      const paymentStatus = await checkUserPaymentStatus();
      
      // If user already has payment method or is in a free role, don't show modal
      if (paymentStatus.hasPaymentMethod || ['admin', 'student'].includes(paymentStatus.role)) {
        setPaymentCompleted(true);
        return;
      }

      // Check if we should skip modal due to recent payment
      const skipModal = sessionStorage.getItem('payment_return_url') || 
                       sessionStorage.getItem('payment_just_completed') || 
                       paymentCompleted;
      
      const response = await axios.get(`${API}/ermt/pricing-check`, { withCredentials: true });
      setPricingInfo(response.data);
      
      // Only show payment modal if explicitly requested and payment is required
      if (response.data.payment_required && showModal && !skipModal) {
        showERMTPaymentCaptureModal({
          message: response.data.message,
          amount: response.data.price,
          service: 'ERMT',
          type: response.data.type || 'payment_required'
        });
      }
    } catch (error) {
      console.error("Pricing check error:", error);
    }
  };

  const loadUsageStats = async () => {
    try {
      const response = await axios.get(`${API}/ermt/results`, { withCredentials: true });
      const results = response.data?.results || [];
      
      const total = results.length;
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const monthly = results.filter(r => new Date(r.created_at) >= startOfMonth).length;
      
      setUsageStats({ total, monthly });
    } catch (error) {
      console.error("Load usage stats error:", error);
      setUsageStats({ total: 0, monthly: 0 });
    }
  };

  const loadUserReports = async () => {
    try {
      const response = await axios.get(`${API}/ermt/results`, { withCredentials: true });
      setReports(response.data.results || []);
    } catch (error) {
      console.error("Load reports error:", error);
      setReports([]); // Set empty array on error
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validExtensions = ['.docx', '.doc', '.txt'];
      const fileName = file.name.toLowerCase();
      const isValid = validExtensions.some(ext => fileName.endsWith(ext));
      
      if (!isValid) {
        toast.error("Please select a DOCX, DOC, or TXT file (PDF not supported for main manuscript)");
        e.target.value = null;
        return;
      }
      
      // Validate file size (20MB)
      if (file.size > 20 * 1024 * 1024) {
        toast.error(`File size must be under 20MB. Your file is ${(file.size / 1024 / 1024).toFixed(2)}MB.`);
        e.target.value = null;
        return;
      }
      
      setSelectedFile(file);
      fileRef.current = file; // Also store in ref
      toast.success(`Manuscript file selected: ${file.name}`);
    }
  };

  const handleFrontMatterChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validExtensions = ['.docx', '.doc', '.txt'];
      const fileName = file.name.toLowerCase();
      const isValid = validExtensions.some(ext => fileName.endsWith(ext));
      
      if (!isValid) {
        toast.error("Please select a DOCX, DOC, or TXT file for front matter");
        e.target.value = null;
        return;
      }
      
      // Validate file size (5MB for front matter)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`Front matter file size must be under 5MB. Your file is ${(file.size / 1024 / 1024).toFixed(2)}MB.`);
        e.target.value = null;
        return;
      }
      
      setSelectedFrontMatter(file);
      frontMatterRef.current = file; // Also store in ref
      toast.success(`Front matter file selected: ${file.name}`);
    }
  };

  const handleCoverImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        toast.error("Please select a JPG, PNG, or WEBP image");
        e.target.value = null;
        return;
      }
      
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`Cover image must be under 5MB. Your file is ${(file.size / 1024 / 1024).toFixed(2)}MB.`);
        e.target.value = null;
        return;
      }
      
      setSelectedCoverImage(file);
      coverImageRef.current = file;
      toast.success(`Cover image selected: ${file.name}`);
    }
  };

  const [isDownloading, setIsDownloading] = useState(false);
  
  const handleDownloadEPUB = async (resultId, bookTitle = 'EPUB_Download') => {
    if (isDownloading) return; // Prevent double-click
    
    setIsDownloading(true);
    const toastId = toast.loading('📥 Preparing your EPUB download...');
    
    try {
      const response = await axios.get(`${API}/ermt/download/${resultId}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Generate safe filename from title
      const safeTitle = bookTitle.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_').substring(0, 50);
      const filename = `${safeTitle}.epub`;
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`✅ Download complete! File saved as: ${filename}`, { 
        id: toastId,
        duration: 5000 
      });
      
    } catch (error) {
      console.error("Download error:", error);
      toast.error(`❌ Download failed: ${error.message}`, { id: toastId });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check payment validation first
    if (!paymentCompleted) {
      toast.error("Payment validation required. Please verify your payment method.");
      setShowPaymentValidation(true);
      return;
    }
    
    // Step 1: Validate required fields
    if (!formData.title || !formData.author_name || !selectedFile) {
      toast.error("Please fill in all required fields (Title, Author) and select a file");
      return;
    }
    
    // Step 2: Check payment verification status
    try {
      // Skip payment validation for beta_tester users
      if (user.role === 'beta_tester') {
        // Beta testers get free access, skip payment checks
        setShowConfirmationModal(true);
        return;
      }
      
      // Check if user needs payment method setup (free_user, freemium, or a_la_carte without payment method)
      if (user.role === 'free_user' || user.role === 'freemium' || (user.role === 'a_la_carte' && !user.payment_method && !user.stripe_customer_id)) {
        setShowPaymentValidation(true); // This will show the Payment Validation Modal
        return;
      }
      
      // Step 3: Payment verification passed - show ERMT price confirmation modal
      setShowConfirmationModal(true);
      
    } catch (error) {
      console.error('🔥 Error checking payment status:', error);
      toast.error("Unable to verify payment status. Please try again.");
    }
  };
  
  const handleConfirmAnalysis = async () => {
    // User confirmed they want to proceed with analysis
    setShowConfirmationModal(false);
    
    // Now submit for analysis
    await submitForAnalysis();
  };
  
  const handleCancelAnalysis = () => {
    // User cancelled - just close modal and return to form
    setShowConfirmationModal(false);
  };
  
  const handleDeleteReport = async (reportId, reportTitle) => {
    setReportToDelete({ id: reportId, title: reportTitle });
    setShowDeleteModal(true);
  };

  const confirmDeleteReport = async () => {
    try {
      const response = await axios.delete(`${API}/ermt/results/${reportToDelete.id}`, { withCredentials: true });
      
      if (response.status === 200) {
        toast.success(`Report "${reportToDelete.title}" deleted successfully`);
        
        // Close modal first
        setShowDeleteModal(false);
        setReportToDelete(null);
        
        // Completely clear all state and refresh the page
        setAnalysisResult(null);
        setReports([]);
        setLoading(true);
        
        // Force complete page refresh to ensure no stale data
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error("Failed to delete report");
      setShowDeleteModal(false);
      setReportToDelete(null);
    }
  };

  const handleSendReceipt = async () => {
    try {
      const response = await axios.post(`${API}/send-receipt-email`, {
        transaction_id: lastTransactionId,
        user_email: user.email
      }, { withCredentials: true });
      toast.success("Receipt email sent successfully!");
      setShowEmailReceiptModal(false);
    } catch (error) {
      console.error('❌ Receipt email error:', error.response?.data || error.message);
      toast.error(`Receipt email not sent: ${error.response?.data?.detail || error.message}`);
      // Still close the modal even if email sending fails
      setShowEmailReceiptModal(false);
    }
  };

  const handleCloseEmailReceiptModal = () => {
    setShowEmailReceiptModal(false);
    toast.success("Analysis completed successfully!");
  };


  const performAnalysis = async () => {
    
    // Check if payment is required and modal isn't already showing
    if (pricingInfo?.payment_required && !showPaymentModal) {
      setShowPaymentModal(true);
      return;
    }
    
    // Proceed with analysis
    await submitForAnalysis();
  };

  const submitForAnalysis = async () => {
    
    // Use file from state or ref backup
    const currentFile = selectedFile || fileRef.current;
    
    if (!currentFile) {
      console.error('🔥 No file selected for analysis');
      toast.error("Please select a file first");
      return;
    }
    
    // Ensure state is synchronized with ref
    if (!selectedFile && fileRef.current) {
      setSelectedFile(fileRef.current);
    }

    
    try {
      setLoading(true);
      setIsProcessing(true); // Show processing modal
      
      // Step 1: Upload
      setCurrentStep('Uploading manuscript and assets...');
      setProgressSteps([
        { text: 'Uploading manuscript', completed: false, current: true },
        { text: 'Converting to EPUB', completed: false, current: false },
        { text: 'Generating final file', completed: false, current: false }
      ]);
      setProcessingProgress(10);
      
      const formDataToSend = new FormData();
      
      // Use form data from state or ref backup
      const currentFormData = formData.title ? formData : (formDataRef.current || formData);
      
      // Add required fields (always add, even if empty)
      formDataToSend.append('title', currentFormData.title || '');
      formDataToSend.append('author_name', currentFormData.author_name || '');
      
      // Add optional fields only if they have values
      if (currentFormData.subtitle) formDataToSend.append('subtitle', currentFormData.subtitle);
      if (currentFormData.publisher) formDataToSend.append('publisher', currentFormData.publisher);
      if (currentFormData.genre) formDataToSend.append('genre', currentFormData.genre);
      if (currentFormData.sub_genre) formDataToSend.append('sub_genre', currentFormData.sub_genre);
      
      formDataToSend.append('file', currentFile);
      
      // Add front matter if selected
      if (selectedFrontMatter) {
        formDataToSend.append('front_matter', selectedFrontMatter);
      }
      
      // Add cover image if selected
      if (selectedCoverImage) {
        formDataToSend.append('cover_image', selectedCoverImage);
      }
      
      setProcessingProgress(25);
      
      // Step 2: Converting
      setCurrentStep('Converting manuscript to EPUB format...');
      setProgressSteps([
        { text: 'Uploading manuscript', completed: true, current: false },
        { text: 'Converting to EPUB', completed: false, current: true },
        { text: 'Generating final file', completed: false, current: false }
      ]);
      setProcessingProgress(40);
      
      const response = await axios.post(`${API}/ermt/upload-and-process`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true,
        timeout: 300000 // 5 minutes for EPUB conversion
      });
      
      // Step 3: Finalizing
      setCurrentStep('Generating publication-ready EPUB...');
      setProgressSteps([
        { text: 'Uploading manuscript', completed: true, current: false },
        { text: 'Converting to EPUB', completed: true, current: false },
        { text: 'Generating final file', completed: false, current: true }
      ]);
      setProcessingProgress(85);
      
      // EPUB Conversion Complete
      const resultId = response.data.id;
      const wasCharged = response.data.charged;
      const transactionId = response.data.transaction_id;
      const chargeAmount = response.data.charge_amount || (user.role === 'subscriber' ? 7.50 : 15.00);
      
      // Automatically load and display the result FIRST
      await loadAndDisplayReport(resultId);
      
      // Complete
      setProgressSteps([
        { text: 'Uploading manuscript', completed: true, current: false },
        { text: 'Converting to EPUB', completed: true, current: false },
        { text: 'Generating final file', completed: true, current: false }
      ]);
      setProcessingProgress(100);
      setCurrentStep('Complete!');
      
      // Then close processing modal
      setIsProcessing(false);
      setPaymentProcessing(false);
      
      // Show success notification
      toast.success("🎉 EPUB conversion completed! Your file is ready for download.", { duration: 5000 });
      
      // Refresh dashboard stats after successful service completion
      window.dispatchEvent(new CustomEvent('refreshDashboardStats'));
      
      // Store transaction info for email receipt (don't show modal if result is displayed)
      if (wasCharged && transactionId) {
        setLastTransactionId(transactionId);
        setLastCharge({
          amount: chargeAmount,
          serviceName: "EPUB-Ready Manuscript Transformer (ERMT)",
          bookTitle: formData.title || "EPUB Conversion"
        });
        // Show email receipt modal after a delay (user can see result first)
        setTimeout(() => {
          setShowEmailReceiptModal(true);
        }, 2000);
      }
      
      // Refresh results list
      loadUserReports();
      
    } catch (error) {
      setIsProcessing(false); // Hide processing modal on error
      console.error('🔥 Analysis error:', error);
      console.error('🔥 Error response:', error.response?.data);
      console.error('🔥 Error status:', error.response?.status);
      
      // Check for API overload error (503 with overloaded_error or overloaded_after_X_attempts)
      const errorDetail = error.response?.data?.detail || error.message || '';
      const errorStatus = error.response?.status;
      
      
      const isOverloadError = (errorStatus === 503) || 
                             (errorDetail && (
                               errorDetail.includes('overloaded') || 
                               errorDetail.includes('Overloaded') ||
                               errorDetail === 'overloaded_error'
                             ));
      
      if (isOverloadError) {
        // Show retry modal - don't lose form data!
        setRetryError(errorDetail);
        // Set attemptNumber to maxAttempts to show "retries failed" state with buttons
        setRetryAttempt(maxRetryAttempts);
        setIsRetrying(false); // Not currently retrying, waiting for user input
        setShowRetryModal(true);
        return; // Don't show error toast, let user retry
      }
      
      if (error.response?.status === 402) {
        // Backend says payment is required - show payment capture modal
        const errorData = error.response.data;
        showERMTPaymentCaptureModal({
          message: errorData.message || 'Payment required for ERMT analysis',
          amount: errorData.amount || 10.0,
          service: 'ERMT',
          type: errorData.type || 'payment_required'
        });
      } else {
        // Analysis failed for other reasons
        const errorMessage = error.response?.data?.detail || error.message || "Analysis failed";
        toast.error(`Analysis Failed: ${errorMessage}`);
      }
      
    } finally {
      setLoading(false);
      setShowPaymentModal(false);
    }
  };

  const initiateDirectPayment = async (packageId) => {
    try {
      
      // Store the current page and form data in sessionStorage for return after payment
      sessionStorage.setItem('payment_return_url', window.location.pathname);
      sessionStorage.setItem('payment_service', packageId);
      
      // Store form data if user was in the middle of filling out ERMT form
      const currentFormData = {
        title: formData.title,
        author_name: formData.author_name,
        subtitle: formData.subtitle,
        publisher: formData.publisher,
        genre: formData.genre,
        sub_genre: formData.sub_genre
      };
      sessionStorage.setItem('ermt_form_data', JSON.stringify(currentFormData));
      
      const response = await axios.post(
        `${API}/payments/checkout/session`,
        {
          package_id: packageId,
          origin_url: window.location.origin
        },
        { withCredentials: true }
      );
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
      
    } catch (error) {
      console.error('🔥 Payment initiation error:', error);
      toast.error(`Payment setup failed: ${error.response?.data?.detail || error.message}`);
    }
  };

  const showERMTPaymentCaptureModal = (errorData) => {
    
    // Remove any existing modals first
    const existingModal = document.querySelector('[data-modal="ermt-payment"]');
    if (existingModal) {
      existingModal.remove();
    }
    
    const modal = document.createElement('div');
    modal.setAttribute('data-modal', 'ermt-payment');
    modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';
    modal.innerHTML = `
      <div class="bg-white p-8 rounded-lg max-w-md w-full mx-4">
        <h3 class="text-xl font-bold mb-4">Premium Service - Payment Required</h3>
        <p class="text-gray-600 mb-4">
          You are currently using the <strong>FREE</strong> version of the app. This service requires payment verification.
        </p>
        <div class="bg-blue-50 p-3 rounded mb-4">
          <p class="text-sm text-blue-800">
            <strong>Service:</strong> ${errorData.service || 'ERMT'}<br>
            <strong>Amount:</strong> $${errorData.amount || '10.00'}
          </p>
        </div>
        
        <div class="mb-6">
          <p class="text-sm font-medium text-gray-700 mb-3">How would you like to continue?</p>
          
          <div class="space-y-3">
            <div class="border rounded-lg p-3 cursor-pointer hover:bg-gray-50" id="option-a-la-carte">
              <div class="flex items-center">
                <input type="radio" name="payment-option" value="a-la-carte" id="radio-a-la-carte" class="mr-3">
                <div>
                  <div class="font-medium">Pay as You Go (A la Carte)</div>
                  <div class="text-sm text-gray-600">Pay only for services you use</div>
                </div>
              </div>
            </div>
            
            <div class="border rounded-lg p-3 cursor-pointer hover:bg-gray-50" id="option-subscription">
              <div class="flex items-center">
                <input type="radio" name="payment-option" value="subscription" id="radio-subscription" class="mr-3">
                <div>
                  <div class="font-medium">Monthly/Annual Subscription</div>
                  <div class="text-sm text-gray-600">Save 50% on premium services + free access to all standard tools</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="flex space-x-4">
          <button id="continue-payment-ermt" class="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex-1 disabled:opacity-50 disabled:cursor-not-allowed" disabled>
            Continue
          </button>
          <button id="cancel-payment-ermt" class="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 flex-1">
            Cancel
          </button>
        </div>
      </div>
    `;
    
    document.body.appendChild(modal);
    
    // Function to safely remove modal
    const safeRemoveModal = () => {
      try {
        if (modal && modal.parentNode) {
          modal.parentNode.removeChild(modal);
        }
        // Also remove any other existing modals to prevent conflicts
        const existingModals = document.querySelectorAll('[data-modal="ermt-payment"]');
        existingModals.forEach(existingModal => {
          if (existingModal.parentNode) {
            existingModal.parentNode.removeChild(existingModal);
          }
        });
      } catch (error) {
        console.error('Error removing modal:', error);
      }
    };
    
    // Handle modal actions with improved event binding
    setTimeout(() => {
      
      const continueButton = document.getElementById('continue-payment-ermt');
      const cancelButton = document.getElementById('cancel-payment-ermt');
      const aLaCarteOption = document.getElementById('option-a-la-carte');
      const subscriptionOption = document.getElementById('option-subscription');
      const radioALaCarte = document.getElementById('radio-a-la-carte');
      const radioSubscription = document.getElementById('radio-subscription');
      
      
      // Handle option selection
      if (aLaCarteOption && radioALaCarte) {
        const aLaCarteHandler = (e) => {
          radioALaCarte.checked = true;
          radioSubscription.checked = false;
          continueButton.disabled = false;
          continueButton.classList.remove('disabled:opacity-50', 'disabled:cursor-not-allowed');
        };
        aLaCarteOption.addEventListener('click', aLaCarteHandler);
        radioALaCarte.addEventListener('change', aLaCarteHandler);
      }
      
      if (subscriptionOption && radioSubscription) {
        const subscriptionHandler = (e) => {
          radioSubscription.checked = true;
          radioALaCarte.checked = false;
          continueButton.disabled = false;
          continueButton.classList.remove('disabled:opacity-50', 'disabled:cursor-not-allowed');
        };
        subscriptionOption.addEventListener('click', subscriptionHandler);
        radioSubscription.addEventListener('change', subscriptionHandler);
      }
      
      if (continueButton) {
        const continueClickHandler = (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          if (continueButton.disabled) return;
          
          const selectedOption = document.querySelector('input[name="payment-option"]:checked')?.value;
          
          if (selectedOption === 'a-la-carte') {
            // Initiate direct payment for ERMT service
            safeRemoveModal();
            initiateDirectPayment('ermt');
          } else if (selectedOption === 'subscription') {
            // Redirect to pricing page for subscription selection
            safeRemoveModal();
            navigate('/prices?action=subscribe');
          } else {
            console.error('No payment option selected');
          }
        };
        
        continueButton.addEventListener('click', continueClickHandler);
      } else {
        console.error('❌ Continue button not found in DOM');
      }
      
      if (cancelButton) {
        const cancelClickHandler = (e) => {
          e.preventDefault();
          e.stopPropagation();
          safeRemoveModal();
        };
        
        cancelButton.addEventListener('click', cancelClickHandler);
      } else {
        console.error('❌ Cancel button not found in DOM');
      }
      
      // Close on outside click
      const outsideClickHandler = (e) => {
        if (e.target === modal) {
          safeRemoveModal();
        }
      };
      
      modal.addEventListener('click', outsideClickHandler);
      
      // Add escape key handler
      const escapeHandler = (e) => {
        if (e.key === 'Escape') {
          safeRemoveModal();
          document.removeEventListener('keydown', escapeHandler);
        }
      };
      
      document.addEventListener('keydown', escapeHandler);
      
    }, 250); // Increased timeout for better reliability
  };

  const handlePaymentAndAnalysis = async () => {
    // Immediately hide the payment modal to prevent double-clicking
    setShowPaymentModal(false);
    setPaymentProcessing(true);
    
    try {
      
      // Create Stripe checkout session
      const checkoutResponse = await axios.post(`${API}/payments/checkout/session`, {
        service: "ermt",
        package_id: "ermt_analysis",
        origin_url: window.location.origin
      }, { withCredentials: true });
      
      
      if (checkoutResponse.data.checkout_url) {
        
        // Open Stripe checkout in a new window/tab
        const stripeWindow = window.open(checkoutResponse.data.checkout_url, '_blank', 'width=800,height=600');
        
        if (stripeWindow) {
          toast.success("Stripe checkout opened in new window. Complete payment there and return here.");
          
          // Monitor for window close
          const checkClosed = setInterval(() => {
            if (stripeWindow.closed) {
              clearInterval(checkClosed);
              toast.info("Payment window closed. If payment was successful, the analysis button will be activated.");
            }
          }, 1000);
          
        } else {
          throw new Error("Popup blocked. Please allow popups and try again.");
        }
      } else {
        console.error('🔥 No checkout_url in response:', checkoutResponse.data);
        throw new Error("Failed to create payment session - no checkout URL");
      }
    } catch (error) {
      console.error('🔥 Payment error:', error);
      console.error('🔥 Error details:', error.response?.data);
      toast.error("Failed to process payment. Please try again.");
      setPaymentProcessing(false);
      setShowPaymentModal(true); // Show modal again if payment fails
    }
  };

  const handleNewManuscript = () => {
    // Reset all form data
    setFormData({
      title: '',
      subtitle: '',
      author_name: '',
      publisher: '',
      genre: '',
      sub_genre: ''
    });
    
    // Clear file state
    setSelectedFile(null);
    setSelectedFrontMatter(null);
    fileRef.current = null; // Also clear ref
    frontMatterRef.current = null; // Also clear front matter ref
    formDataRef.current = null; // Clear form data ref
    
    // Reset payment states
    setPaymentCompleted(false);
    setPaymentProcessing(false);
    
    // Clear any analysis results
    setAnalysisResult(null);
    setLoading(false);
    
    // Clear file inputs
    const fileInputs = document.querySelectorAll('input[type="file"]');
    fileInputs.forEach(input => {
      input.value = '';
    });
    
    // Scroll to top of page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const loadAndDisplayReport = async (reportId) => {
    try {
      // Set loading state and clear previous results
      setLoading(true);
      setAnalysisResult(null);
      
      const response = await axios.get(`${API}/ermt/results/${reportId}`, { withCredentials: true });
      
      // Set ERMT-specific data only
      setAnalysisResult({
        id: response.data.id,
        title: response.data.title,
        subtitle: response.data.subtitle,
        author_name: response.data.author_name,
        publisher: response.data.publisher,
        genre: response.data.genre,
        sub_genre: response.data.sub_genre,
        source_format: response.data.source_format,
        word_count: response.data.word_count,
        page_count: response.data.page_count,
        validation_status: response.data.validation_status,
        validation_report: response.data.validation_report,
        created_at: response.data.created_at
      });
      
      setLoading(false);
      
      // Scroll to results
      setTimeout(() => {
        const resultsElement = document.querySelector('[data-results-section]');
        if (resultsElement) {
          resultsElement.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } catch (error) {
      toast.error("Failed to load report");
      console.error("Load report error:", error);
      setLoading(false);
    }
  };

  const exportToPDF = () => {
    if (!analysisResult) {
      toast.error("No analysis result to export");
      return;
    }
    
    try {
      toast.info("Generating PDF... Please wait");
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 170;
      const leftMargin = 20;
      let yPosition = 20;
      
      // Helper function for page breaks
      const checkNewPage = (spaceNeeded) => {
        if (yPosition + spaceNeeded > 280) {
          pdf.addPage();
          yPosition = 20;
        }
      };
      
      // Helper function to add multi-line text
      const addMultiLineText = (text, fontSize = 10, maxWidth = pageWidth) => {
        if (!text) return;
        pdf.setFontSize(fontSize);
        const lines = pdf.splitTextToSize(text, maxWidth);
        lines.forEach(line => {
          checkNewPage(7);
          pdf.text(line, leftMargin, yPosition);
          yPosition += 6;
        });
      };
      
      // HEADER
      const timestamp = new Date().toLocaleString('en-US', {
        month: '2-digit', day: '2-digit', year: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: true
      });
      
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'normal');
      pdf.setTextColor(100, 100, 100);
      pdf.text(timestamp, leftMargin, yPosition);
      pdf.text('Digital Ink AI - ERMT Report', 190, yPosition, { align: 'right' });
      yPosition += 10;
      
      pdf.setFontSize(20);
      pdf.setFont(undefined, 'bold');
      pdf.setTextColor(30, 64, 175);
      pdf.text('DIGITAL INK AI', 105, yPosition, { align: 'center' });
      yPosition += 10;
      
      pdf.setFontSize(11);
      pdf.setFont(undefined, 'italic');
      pdf.setTextColor(100, 100, 100);
      pdf.text('Book Viability & Digital Marketing Analysis', 105, yPosition, { align: 'center' });
      yPosition += 15;
      
      // BOOK INFO
      pdf.setFontSize(16);
      pdf.setFont(undefined, 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text(analysisResult.title || '', 105, yPosition, { align: 'center' });
      yPosition += 8;
      
      if (analysisResult.subtitle) {
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'italic');
        pdf.text(analysisResult.subtitle, 105, yPosition, { align: 'center' });
        yPosition += 7;
      }
      
      pdf.setFontSize(12);
      pdf.setFont(undefined, 'normal');
      pdf.text(`by ${analysisResult.author_name || ''}`, 105, yPosition, { align: 'center' });
      yPosition += 15;
      
      // SCORES
      checkNewPage(30);
      pdf.setFontSize(12);
      pdf.setFont(undefined, 'bold');
      pdf.text('Quality Scores', leftMargin, yPosition);
      yPosition += 7;
      
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'normal');
      pdf.text(`Viability Score: ${analysisResult.viability_score}/10`, leftMargin + 5, yPosition);
      yPosition += 6;
      if (analysisResult.technical_score) {
        pdf.text(`Technical Writing: ${analysisResult.technical_score}/10`, leftMargin + 5, yPosition);
        yPosition += 6;
      }
      if (analysisResult.content_quality) {
        pdf.text(`Content Depth & Quality: ${analysisResult.content_quality}/10`, leftMargin + 5, yPosition);
        yPosition += 6;
      }
      pdf.setFont(undefined, 'bold');
      pdf.text(`Overall Quality: ${analysisResult.overall_score}/10`, leftMargin + 5, yPosition);
      yPosition += 10;
      
      pdf.setFont(undefined, 'normal');
      
      // NARRATIVE SUMMARY
      if (analysisResult.narrative_summary) {
        checkNewPage(20);
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.text('Narrative Summary', leftMargin, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        addMultiLineText(analysisResult.narrative_summary);
        yPosition += 5;
      }
      
      // SCORE EXPLANATIONS
      if (analysisResult.score_explanations) {
        checkNewPage(20);
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.text('Score Explanations', leftMargin, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        addMultiLineText(analysisResult.score_explanations);
        yPosition += 5;
      }
      
      // CONTENT OBSERVATIONS
      if (analysisResult.content_observations) {
        checkNewPage(20);
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.text('Content Observations', leftMargin, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        addMultiLineText(analysisResult.content_observations);
        yPosition += 5;
      }
      
      // COMPARABLE TITLES
      if (analysisResult.comparable_titles) {
        checkNewPage(20);
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.text('Comparable Titles', leftMargin, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        addMultiLineText(analysisResult.comparable_titles);
        yPosition += 5;
      }
      
      // DEVELOPMENT RECOMMENDATIONS
      if (analysisResult.development_recommendations) {
        checkNewPage(20);
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.text('Development Recommendations', leftMargin, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        addMultiLineText(analysisResult.development_recommendations);
        yPosition += 5;
      }
      
      // MARKET POSITIONING
      if (analysisResult.market_positioning) {
        checkNewPage(20);
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.text('Market Positioning', leftMargin, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        addMultiLineText(analysisResult.market_positioning);
        yPosition += 5;
      }
      
      // MONETIZATION STRATEGY
      if (analysisResult.monetization_strategy) {
        checkNewPage(20);
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.text('Monetization & Pricing Strategy', leftMargin, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        addMultiLineText(analysisResult.monetization_strategy);
        yPosition += 5;
      }
      
      // MARKETING ROADMAP
      if (analysisResult.marketing_roadmap) {
        checkNewPage(20);
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.text('Marketing & Publishing Roadmap', leftMargin, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        addMultiLineText(analysisResult.marketing_roadmap);
        yPosition += 5;
      }
      
      // DIGITAL MARKETING ASSETS
      if (analysisResult.digital_marketing_assets) {
        checkNewPage(20);
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.text('Digital Marketing Assets', leftMargin, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        addMultiLineText(analysisResult.digital_marketing_assets);
        yPosition += 5;
      }
      
      // Footer on all pages
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text(`Page ${i} of ${totalPages}`, 105, 287, { align: 'center' });
        pdf.text('www.DigitalInkAI.com', 105, 292, { align: 'center' });
      }
      
      const filename = `ERMT_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(filename);
      toast.success("PDF downloaded successfully!");
    } catch (error) {
      console.error('PDF error:', error);
      toast.error(`Failed to generate PDF: ${error.message}`);
    }
  };

  const printResults = () => {
    if (!analysisResult) return;
    
    const printWindow = window.open('', '_blank');
    const resultsElement = document.getElementById('ermt-results');
    
    if (resultsElement && printWindow) {
      // Format timestamp for header
      const now = new Date();
      const timestamp = now.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      const generatedDate = now.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
      
      printWindow.document.write(`
        <html>
          <head>
            <title>Digital Ink AI - ERMT Report</title>
            <style>
              body { font-family: 'Times New Roman', Times, serif; font-size: 12pt; margin: 15px; line-height: 1.5; }
              .print-header { display: flex; justify-content: space-between; margin-bottom: 20px; font-size: 10pt; color: #666; }
              .ermt-header { text-align: center; margin-bottom: 20px; }
              .ermt-header img { height: 80px; margin: 0 auto 10px; display: block; }
              .ermt-header h1 { font-size: 16pt; margin: 8px 0; font-family: 'Times New Roman', Times, serif; color: #1e40af; font-style: italic; }
              .ermt-header h2 { font-size: 18pt; margin: 5px 0; font-family: 'Times New Roman', Times, serif; font-weight: bold; }
              .ermt-header p { font-size: 11pt; margin: 3px 0; color: #666; }
              .generated-date { text-align: center; font-size: 10pt; color: #666; margin-top: 15px; }
              .metrics-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin: 15px 0; }
              .metric-card { border: 1px solid #ddd; padding: 10px; text-align: center; border-radius: 6px; }
              .metric-score { font-size: 18pt; font-weight: bold; margin-bottom: 3px; }
              .metric-label { font-size: 10pt; color: #666; }
              .detailed-scores { margin: 12px 0; padding: 10px; background-color: #f8fafc; border: 1px solid #ddd; border-radius: 6px; }
              .detailed-scores h3 { font-size: 14pt; font-weight: bold; margin: 0 0 8px 0; color: #1e293b; }
              .score-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; }
              .score-item { display: flex; justify-content: space-between; padding: 8px; background-color: #fff; border-radius: 4px; border: 1px solid #e2e8f0; }
              .score-item.overall { background-color: #dbeafe; border-color: #3b82f6; }
              .score-label { font-size: 12pt; color: #475569; }
              .score-value { font-size: 12pt; font-weight: bold; color: #1e293b; }
              .report-content { white-space: pre-wrap; font-family: 'Times New Roman', Times, serif; line-height: 1.5; }
              .section { margin-bottom: 10px; padding: 10px; border-left: 4px solid; border-radius: 6px; page-break-inside: avoid; }
              .section h3 { font-size: 14pt; font-weight: bold; margin: 0 0 6px 0; font-family: 'Times New Roman', Times, serif; }
              .section p { margin: 0; font-size: 12pt; line-height: 1.4; font-family: 'Times New Roman', Times, serif; }
              .manuscript-info { margin-bottom: 12px; padding-bottom: 10px; border-bottom: 1px solid #ddd; }
              .manuscript-info h2 { font-size: 16pt; font-weight: bold; margin: 0 0 3px 0; font-family: 'Times New Roman', Times, serif; }
              .manuscript-info p { font-size: 12pt; margin: 3px 0; font-family: 'Times New Roman', Times, serif; }
              .footer { margin-top: 15px; text-align: center; font-size: 10pt; color: #666; border-top: 1px solid #ddd; padding-top: 12px; }
              @media print { body { margin: 10px; } .no-print { display: none; } }
            </style>
          </head>
          <body>
            <!-- Standard Header -->
            <div class="print-header">
              <div>${timestamp}</div>
              <div>Digital Ink AI - ERMT Report</div>
            </div>
            
            <div class="ermt-header">
              <img src="/Digital_Ink_AI_Logo.png" alt="Digital Ink AI" onload="window.print();" />
              <h1>EPUB-Ready Manuscript Transformer</h1>
              <h2>${analysisResult.title}</h2>
              <p>by ${analysisResult.author_name}</p>
            </div>
            
            <div class="generated-date">Generated ${generatedDate}</div>
            
            <div class="metrics-grid">
              <div class="metric-card">
                <div class="metric-score" style="color: #2563eb;">${analysisResult.overall_score}/10</div>
                <div class="metric-label">Overall Score</div>
              </div>
              <div class="metric-card">
                <div class="metric-score" style="color: #16a34a;">${analysisResult.viability_score}/10</div>
                <div class="metric-label">Viability Score</div>
              </div>
              <div class="metric-card">
                <div class="metric-score" style="color: #9333ea;">${analysisResult.word_count.toLocaleString()}</div>
                <div class="metric-label">Words</div>
              </div>
              <div class="metric-card">
                <div class="metric-score" style="color: #ea580c;">${analysisResult.page_count}</div>
                <div class="metric-label">Pages</div>
              </div>
            </div>
            
            <!-- Manuscript Title and Author -->
            <div class="manuscript-info">
              <h2>${analysisResult.title}</h2>
              ${analysisResult.subtitle ? `<p style="font-style: italic; color: #475569;">${analysisResult.subtitle}</p>` : ''}
              <p style="font-weight: 600;">by ${analysisResult.author_name}</p>
              ${analysisResult.publisher ? `<p style="font-size: 12px; color: #64748b; margin-top: 5px;">Publisher: ${analysisResult.publisher}</p>` : ''}
            </div>
            
            <!-- Detailed Analysis Scores -->
            <div class="detailed-scores">
              <h3>Detailed Analysis Scores</h3>
              <div class="score-grid">
                <div class="score-item">
                  <span class="score-label">Market Viability:</span>
                  <span class="score-value">${analysisResult.viability_score}/10</span>
                </div>
                ${analysisResult.technical_score ? `
                <div class="score-item">
                  <span class="score-label">Technical Writing:</span>
                  <span class="score-value">${analysisResult.technical_score}/10</span>
                </div>` : ''}
                ${analysisResult.content_quality ? `
                <div class="score-item">
                  <span class="score-label">Content Depth & Quality:</span>
                  <span class="score-value">${analysisResult.content_quality}/10</span>
                </div>` : ''}
                <div class="score-item overall">
                  <span class="score-label" style="font-weight: 600;">Overall Quality:</span>
                  <span class="score-value" style="color: #2563eb; font-size: 15px;">${analysisResult.overall_score}/10</span>
                </div>
              </div>
            </div>
            
            <!-- 8 Structured Sections -->
            <div class="report-content">
              
              ${analysisResult.narrative_summary ? `
              <div class="section" style="background-color: #f8fafc; border-left-color: #3b82f6;">
                <h3 style="color: #1e293b;">📖 Narrative Summary</h3>
                <p style="color: #334155;">${analysisResult.narrative_summary}</p>
              </div>` : ''}
              
              ${analysisResult.score_explanations ? `
              <div class="section" style="background-color: #fef3c7; border-left-color: #f59e0b;">
                <h3 style="color: #92400e;">📊 Score Explanations</h3>
                <p style="color: #78350f;">${analysisResult.score_explanations}</p>
              </div>` : ''}
              
              ${analysisResult.content_observations ? `
              <div class="section" style="background-color: #fce7f3; border-left-color: #ec4899;">
                <h3 style="color: #831843;">🔍 Content Observations</h3>
                <p style="color: #9f1239;">${analysisResult.content_observations}</p>
              </div>` : ''}
              
              ${analysisResult.comparable_titles ? `
              <div class="section" style="background-color: #dbeafe; border-left-color: #2563eb;">
                <h3 style="color: #1e3a8a;">📚 Comparable Titles</h3>
                <p style="color: #1e40af;">${analysisResult.comparable_titles}</p>
              </div>` : ''}
              
              ${analysisResult.development_recommendations ? `
              <div class="section" style="background-color: #dcfce7; border-left-color: #16a34a;">
                <h3 style="color: #14532d;">💡 Development Recommendations</h3>
                <p style="color: #15803d;">${analysisResult.development_recommendations}</p>
              </div>` : ''}
              
              ${analysisResult.market_positioning ? `
              <div class="section" style="background-color: #f3e8ff; border-left-color: #9333ea;">
                <h3 style="color: #581c87;">🎯 Market Positioning</h3>
                <p style="color: #6b21a8;">${analysisResult.market_positioning}</p>
              </div>` : ''}
              
              ${analysisResult.monetization_strategy ? `
              <div class="section" style="background-color: #ccfbf1; border-left-color: #14b8a6;">
                <h3 style="color: #134e4a;">💰 Monetization & Pricing Strategy</h3>
                <p style="color: #115e59;">${analysisResult.monetization_strategy}</p>
              </div>` : ''}
              
              ${analysisResult.marketing_roadmap ? `
              <div class="section" style="background-color: #e0e7ff; border-left-color: #6366f1;">
                <h3 style="color: #312e81;">📢 Marketing & Publishing Roadmap</h3>
                <p style="color: #3730a3;">${analysisResult.marketing_roadmap}</p>
              </div>` : ''}
              
              ${analysisResult.digital_marketing_assets ? `
              <div class="section" style="background-color: #fef3c7; border-left-color: #f59e0b;">
                <h3 style="color: #92400e;">🎯 Digital Marketing Assets</h3>
                <p style="color: #78350f;">${analysisResult.digital_marketing_assets}</p>
              </div>` : ''}
              
              <!-- Commercial Assessment -->
              <div class="section" style="background-color: #ecfdf5; border-left-color: #10b981;">
                <h3 style="color: #065f46;">💰 Commercial Assessment</h3>
                <p style="color: #047857; margin-bottom: 5px;"><strong>Commercial Viability Rating:</strong> ${analysisResult.viability_score}/10</p>
                <p style="color: #047857; margin: 0;"><strong>Success Probability:</strong> ${Math.round((analysisResult.overall_score + analysisResult.viability_score) * 5)}% likelihood of commercial success</p>
              </div>
              
            </div>
            
            <div class="footer">
              <p>Generated with Digital Ink AI - www.DigitalInkAI.com</p>
            </div>
          </body>
        </html>
      `);
      
      printWindow.document.close();
      printWindow.focus();
      // Print will be triggered by image onload
    }
  };

  // Use central MST formatter

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Header with Logo and Navigation - Hidden when viewing results */}
      {!analysisResult && (
        <header className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <img 
                  src="/Digital_Ink_AI_Logo.png" 
                  alt="Digital Ink AI Logo" 
                  className="h-12 w-auto"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png";
                  }}
                />
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate('/homepage')}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  🏠 Home
                </button>
                <button
                  onClick={() => navigate('/user_dashboard')}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  📊 Dashboard
                </button>
              </div>
            </div>
          </div>
        </header>
      )}

      <main className="max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Form Page */}
        {!analysisResult && !loading && (
          <>
            {/* Service Title and Description */}
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-slate-900 mb-4">
                📚 EPUB-Ready Manuscript Transformer
              </h1>
              <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
                Transform manuscripts into professionally formatted EPUB files. Industry-standard conversion pipeline uses Pandoc and EPUBCheck to convert PDF, DOCX, DOC, or TXT into validated EPUB3 format with embedded metadata, table of contents, optional cover integration. Receive publication-ready digital files meeting all major e-reader and platform specifications instantly.
              </p>
            </div>

            {/* Pricing Info */}
            {pricingInfo && (
              <div className="mb-8 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-6">
                <div className="text-center">
                  {pricingInfo.payment_required ? (
                    <div>
                      <h3 className="text-xl font-bold text-blue-900 mb-2">💎 Standard Service - ${pricingInfo.price}</h3>
                      <p className="text-blue-700">Professional file conversion charged to your payment method on file</p>
                    </div>
                  ) : (
                    <div>
                      <h3 className="text-xl font-bold text-green-900 mb-2">✨ Unlimited Free Access</h3>
                      <p className="text-green-700">Included with your subscription - convert as many files as you need!</p>
                    </div>
                  )}
                </div>
              </div>
            )}

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Analysis Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <div className="text-center mb-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png" 
                    alt="Digital Ink AI" 
                    className="h-10 mx-auto mb-3"
                  />
                  <CardTitle className="text-2xl text-slate-900">📚 EPUB Manuscript Conversion</CardTitle>
                  <p className="text-sm text-slate-600 mt-1">Convert your manuscript to professional EPUB format</p>
                </div>
              </CardHeader>
              <CardContent>
                {!paymentCompleted ? (
                  // Payment validation in progress - show loading or payment modal
                  <div className="text-center py-8">
                    <div className="animate-pulse">
                      <div className="w-12 h-12 bg-blue-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-700 mb-2">Validating Service Access</h3>
                    <p className="text-slate-600 mb-4">Please wait while we verify your payment information...</p>
                    <Button 
                      onClick={validateServiceAccess}
                      variant="outline"
                      className="mt-4"
                    >
                      Retry Validation
                    </Button>
                  </div>
                ) : (
                  // Payment completed - show full form
                  <div>
                    {/* New Manuscript Button */}
                    <div className="mb-6 flex justify-start">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleNewManuscript}
                    className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                  >
                    📚 New EPUB Conversion
                  </Button>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Manuscript Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="title">Title *</Label>
                      <Input
                        id="title"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                        className="mt-1"
                        placeholder="Enter your book title"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="subtitle">Subtitle</Label>
                      <Input
                        id="subtitle"
                        name="subtitle"
                        value={formData.subtitle}
                        onChange={handleInputChange}
                        className="mt-1"
                        placeholder="Enter subtitle (optional)"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="author_name">Author Name *</Label>
                      <Input
                        id="author_name"
                        name="author_name"
                        value={formData.author_name}
                        onChange={handleInputChange}
                        required
                        className="mt-1"
                        placeholder="Author's full name"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="publisher">Publisher</Label>
                      <Input
                        id="publisher"
                        name="publisher"
                        value={formData.publisher}
                        onChange={handleInputChange}
                        className="mt-1"
                        placeholder="Publisher (if applicable)"
                      />
                    </div>
                  </div>

                  {/* File Format Information */}
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
                    <h4 className="font-semibold text-blue-900 mb-2">📄 Supported File Formats</h4>
                    <p className="text-sm text-blue-800 mb-2">
                      ERMT accepts <strong>Microsoft Word documents (.docx, .doc)</strong> and <strong>plain text files (.txt)</strong> only. 
                      PDF files are not supported because they are rendered formats that make it difficult to extract clean, properly structured text.
                    </p>
                    <p className="text-sm text-blue-800">
                      Word documents preserve the original structure, formatting, and text flow of your manuscript, 
                      ensuring the highest quality EPUB conversion with properly formatted chapters, paragraphs, and sections.
                    </p>
                  </div>

                  {/* Front Matter Upload (OPTIONAL) - NOW FIRST */}
                  <div>
                    <Label htmlFor="frontmatter">Front Matter (Optional)</Label>
                    <div className="mt-2 mb-3 p-3 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-700 mb-2">
                        <strong>Front matter</strong> includes the pages that come before your main content: copyright page, 
                        dedication, table of contents, acknowledgments, foreword, preface, or introduction.
                      </p>
                      <p className="text-sm text-slate-600">
                        Upload this separately if you maintain it in a different document with its own formatting. 
                        If you don't have separate front matter, you can skip this and upload just your manuscript.
                      </p>
                    </div>
                    <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                      <input
                        type="file"
                        id="frontmatter"
                        ref={frontMatterRef}
                        accept=".doc,.docx,.txt"
                        onChange={handleFrontMatterChange}
                        className="hidden"
                      />
                      <label htmlFor="frontmatter" className="cursor-pointer">
                        <div className="text-4xl mb-4">📋</div>
                        {selectedFrontMatter ? (
                          <div>
                            <p className="text-green-600 font-medium">{selectedFrontMatter.name}</p>
                            <p className="text-sm text-slate-500">
                              {(selectedFrontMatter.size / (1024 * 1024)).toFixed(2)} MB
                            </p>
                          </div>
                        ) : (
                          <div>
                            <p className="text-slate-600 font-medium">Click to select front matter (optional)</p>
                            <p className="text-sm text-slate-500">DOCX, DOC, or TXT (max 5MB)</p>
                          </div>
                        )}
                      </label>
                    </div>
                  </div>

                  {/* Manuscript Upload (REQUIRED) - NOW SECOND */}
                  <div>
                    <Label htmlFor="manuscript">Manuscript Contents (Required) *</Label>
                    <div className="mt-2 mb-3 p-3 bg-emerald-50 rounded-lg">
                      <p className="text-sm text-slate-700 mb-2">
                        <strong>Manuscript contents</strong> is your main book content: all chapters, sections, and body text. 
                        This is the core of your book—the story, information, or content your readers came for.
                      </p>
                      <p className="text-sm text-slate-600">
                        Your manuscript should be formatted with clear chapter headings (e.g., "Chapter 1", "Chapter One", "Part I"). 
                        The system will automatically detect these and create a navigable table of contents in your EPUB.
                      </p>
                    </div>
                    <div className="border-2 border-dashed border-emerald-300 rounded-lg p-6 text-center">
                      <input
                        type="file"
                        id="manuscript"
                        ref={fileRef}
                        accept=".doc,.docx,.txt"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                      <label htmlFor="manuscript" className="cursor-pointer">
                        <div className="text-4xl mb-4">📖</div>
                        {selectedFile ? (
                          <div>
                            <p className="text-green-600 font-medium">{selectedFile.name}</p>
                            <p className="text-sm text-slate-500">
                              {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                            </p>
                          </div>
                        ) : (
                          <div>
                            <p className="text-slate-600 font-medium">Click to select your manuscript</p>
                            <p className="text-sm text-slate-500">DOCX, DOC, or TXT (max 20MB)</p>
                          </div>
                        )}
                      </label>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:bg-gray-400 text-white font-bold py-4 px-6 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={loading || paymentProcessing || !paymentCompleted}
                  >
                    {loading ? 'Converting to EPUB...' : paymentProcessing ? 'Processing Payment...' : 'Convert to EPUB with Digital Ink AI'}
                  </button>
                  
                  {/* Payment Validation Warning */}
                  {serviceAccessDenied && !paymentCompleted && (
                    <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-yellow-800 text-sm text-center">
                        ⚠️ Payment method required to access this service. Please add a payment method to continue.
                      </p>
                      <div className="flex justify-center mt-2">
                        <Button
                          onClick={validateServiceAccess}
                          className="bg-yellow-600 hover:bg-yellow-700 text-white"
                          size="sm"
                        >
                          Verify Payment Method
                        </Button>
                      </div>
                    </div>
                  )}
                </form>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Conversion Features */}
            <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-4">🎯 Conversion Features</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start">
                  <span className="text-rose-500 mr-2">✓</span>
                  <span>Smart TOC removal</span>
                </div>
                <div className="flex items-start">
                  <span className="text-rose-500 mr-2">✓</span>
                  <span>Hierarchical structure preservation</span>
                </div>
                <div className="flex items-start">
                  <span className="text-rose-500 mr-2">✓</span>
                  <span>Header/footer cleanup</span>
                </div>
                <div className="flex items-start">
                  <span className="text-rose-500 mr-2">✓</span>
                  <span>Navigable table of contents</span>
                </div>
                <div className="flex items-start">
                  <span className="text-rose-500 mr-2">✓</span>
                  <span>EPUBCheck validation</span>
                </div>
                <div className="flex items-start">
                  <span className="text-rose-500 mr-2">✓</span>
                  <span>Embedded metadata preservation</span>
                </div>
                <div className="flex items-start">
                  <span className="text-rose-500 mr-2">✓</span>
                  <span>Universal e-reader compatibility</span>
                </div>
              </div>
            </div>

            {/* Recent Conversions */}
            {reports.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg text-slate-900">📚 Recent Conversions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {reports.slice(0, 5).map((report, index) => (
                      <div key={index} className="p-3 bg-slate-50 rounded border hover:shadow-md transition-shadow">
                        <div className="font-medium text-sm truncate text-slate-900">{report.title}</div>
                        <div className="text-xs text-slate-500 mt-1">
                          {report.author_name && <span>by {report.author_name}</span>}
                        </div>
                        <div className="text-xs text-slate-400 mb-2">
                          {report.created_at_display || 'Recently'}
                        </div>
                        {report.validation_status && (
                          <Badge className={`mb-2 text-xs ${
                            report.validation_status === 'completed' 
                              ? 'bg-green-100 text-green-800 border-green-300' 
                              : 'bg-amber-100 text-amber-800 border-amber-300'
                          }`}>
                            {report.validation_status === 'completed' ? '✓ Validated' : 'Processing'}
                          </Badge>
                        )}
                        <div className="flex gap-2 mt-2">
                          <Button 
                            size="sm" 
                            className={`flex-1 bg-rose-600 hover:bg-rose-700 text-white ${isDownloading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            onClick={() => handleDownloadEPUB(report.id, report.title)}
                            disabled={isDownloading}
                          >
                            {isDownloading ? (
                              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            ) : (
                              <Download className="w-3 h-3 mr-1" />
                            )}
                            {isDownloading ? 'Downloading...' : 'Download EPUB'}
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="border-red-200 text-red-600 hover:bg-red-50"
                            onClick={() => handleDeleteReport(report.id, report.title)}
                          >
                            🗑️
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full mt-3 text-slate-600 hover:text-slate-900"
                    onClick={() => navigate('/user_dashboard')}
                  >
                    View All Conversions in Dashboard
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
        </>
        )}

        {/* Loading indicator when fetching report */}
        {loading && !analysisResult && (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin inline-block w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full mb-4"></div>
              <p className="text-lg text-slate-600">Loading your ERMT report...</p>
            </div>
          </div>
        )}

        {/* Analysis Results - BCTG-style Professional Layout */}
        {analysisResult && (
          <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-blue-50 to-slate-100">
            {/* Sticky Header with Logo and Navigation */}
            <header className="bg-white shadow-sm border-b print:hidden sticky top-0 z-50">
              <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <img 
                      src="/Digital_Ink_AI_Logo.png" 
                      alt="Digital Ink AI Logo" 
                      className="h-12 w-auto"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png";
                      }}
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate('/homepage')}
                      className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      🏠 Home
                    </button>
                    <button
                      onClick={() => navigate('/user_dashboard')}
                      className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      📊 Dashboard
                    </button>
                  </div>
                </div>
              </div>
            </header>

            {/* Main Content */}
            <main className="max-w-5xl mx-auto px-6 py-8">
              <div className="bg-white rounded-lg shadow-xl p-8">
                {/* Success Header */}
                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
                    <CheckCircle className="w-12 h-12 text-green-600" />
                  </div>
                  <h1 className="text-3xl font-bold text-slate-900 mb-2">✅ EPUB Conversion Complete!</h1>
                  <p className="text-lg text-slate-600">Your manuscript has been successfully converted to EPUB format</p>
                </div>

                {/* Book Details Card */}
                <Card className="bg-gradient-to-r from-blue-50 to-emerald-50 border-2 border-blue-200 mb-6">
                  <CardHeader>
                    <CardTitle className="text-2xl font-bold text-slate-900">📚 Book Information</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div>
                        <span className="text-sm text-slate-600 font-semibold">Title:</span>
                        <p className="text-lg font-bold text-slate-900">{analysisResult.title}</p>
                      </div>
                      {analysisResult.subtitle && (
                        <div>
                          <span className="text-sm text-slate-600 font-semibold">Subtitle:</span>
                          <p className="text-base text-slate-800">{analysisResult.subtitle}</p>
                        </div>
                      )}
                      <div>
                        <span className="text-sm text-slate-600 font-semibold">Author:</span>
                        <p className="text-base text-slate-800">{analysisResult.author_name}</p>
                      </div>
                      {analysisResult.publisher && (
                        <div>
                          <span className="text-sm text-slate-600 font-semibold">Publisher:</span>
                          <p className="text-base text-slate-800">{analysisResult.publisher}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Conversion Details */}
                <Card className="bg-white border-slate-200 mb-6">
                  <CardHeader className="bg-gradient-to-r from-slate-50 to-purple-50">
                    <CardTitle className="text-lg text-slate-900">📊 Conversion Details</CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div className="text-center p-4 bg-blue-50 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600">{analysisResult.word_count?.toLocaleString() || 'N/A'}</div>
                        <div className="text-sm text-slate-600 mt-1">Words</div>
                      </div>
                      <div className="text-center p-4 bg-green-50 rounded-lg">
                        <div className="text-2xl font-bold text-green-600">{analysisResult.page_count || 'N/A'}</div>
                        <div className="text-sm text-slate-600 mt-1">Est. Pages</div>
                      </div>
                      <div className="text-center p-4 bg-purple-50 rounded-lg">
                        <div className="text-lg font-bold text-purple-600">{analysisResult.source_format?.toUpperCase() || 'N/A'}</div>
                        <div className="text-sm text-slate-600 mt-1">Source Format</div>
                      </div>
                    </div>

                    {/* Validation Status */}
                    {analysisResult.validation_status && (
                      <div className="mt-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-emerald-600" />
                          <span className="font-semibold text-emerald-900">Status: {analysisResult.validation_status}</span>
                        </div>
                        {analysisResult.validation_report && (
                          <p className="text-sm text-emerald-700 mt-2">{analysisResult.validation_report}</p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Download Button - PROMINENT */}
                <div className="bg-gradient-to-r from-blue-600 to-emerald-600 rounded-lg p-8 text-center mb-6">
                  <h2 className="text-2xl font-bold text-white mb-4">📥 Your EPUB is Ready!</h2>
                  <button
                    onClick={() => handleDownloadEPUB(analysisResult.id, analysisResult.title)}
                    disabled={isDownloading}
                    className={`inline-flex items-center gap-3 px-8 py-4 bg-white text-blue-700 font-bold text-lg rounded-lg transform transition shadow-lg ${isDownloading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100 hover:scale-105'}`}
                  >
                    {isDownloading ? (
                      <>
                        <Loader2 className="w-6 h-6 animate-spin" />
                        Downloading...
                      </>
                    ) : (
                      <>
                        <Download className="w-6 h-6" />
                        Download EPUB File
                      </>
                    )}
                  </button>
                  <p className="text-white text-sm mt-4 opacity-90">Click to download your professionally formatted EPUB file</p>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={handleNewManuscript}
                    className="px-6 py-3 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition"
                  >
                    📚 Convert Another Manuscript
                  </button>
                  <button
                    onClick={() => navigate('/user_dashboard')}
                    className="px-6 py-3 bg-slate-600 text-white font-semibold rounded-lg hover:bg-slate-700 transition"
                  >
                    📊 View All Conversions
                  </button>
                </div>

                {/* Conversion Date */}
                {analysisResult.created_at && (
                  <div className="text-center mt-6 text-sm text-slate-500">
                    Converted on {formatTimestamp(analysisResult.created_at)}
                  </div>
                )}
              </div>
            </main>
          </div>
        )}
      </main>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-medium mb-4">💳 Payment Required</h3>
              <p className="text-slate-600 mb-4">
                ERMT analysis costs ${pricingInfo?.price} for detailed manuscript evaluation.
              </p>
              
              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                <h4 className="font-medium text-blue-900 mb-2">What You'll Receive:</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Comprehensive 4-phase analysis</li>
                  <li>• Quality scores and market assessment</li>
                  <li>• Development recommendations</li>
                  <li>• Commercial potential evaluation</li>
                  <li>• Professional PDF report</li>
                </ul>
              </div>
              
              <div className="flex space-x-3">
                <Button 
                  onClick={handlePaymentAndAnalysis}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  Pay ${pricingInfo?.price} & Analyze
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setShowPaymentModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
              
              <p className="text-xs text-slate-500 text-center mt-3">
                Secure payment processed via Stripe
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal for Paid Analysis */}
      {showConfirmationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="text-center">
              <div className="text-4xl mb-4">💎</div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">
                ERMT Analysis - Confirm & Proceed
              </h3>
              
              {user.subscription_status && user.subscription_status !== "None" ? (
                // Has active subscription - 50% discount
                <div className="text-left bg-green-50 border border-green-200 rounded p-4 mb-4">
                  <p className="text-sm text-green-800 mb-2">
                    <strong>🎉 Your Subscriber Benefits:</strong>
                  </p>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-green-700">Regular Price:</span>
                    <span className="text-sm text-green-700 line-through">$15.00</span>
                  </div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-green-700">Your Discount:</span>
                    <span className="text-sm font-semibold text-green-700">50% OFF</span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-green-300">
                    <span className="text-base font-bold text-green-900">Your Price:</span>
                    <span className="text-base font-bold text-green-900">$7.50</span>
                  </div>
                </div>
              ) : (user.role === "admin" || user.role === "beta_tester") ? (
                // Admin/Beta Tester - Free
                <div className="text-left bg-purple-50 border border-purple-200 rounded p-4 mb-4">
                  <div className="flex justify-between items-center">
                    <span className="text-base font-bold text-purple-900">Service Fee:</span>
                    <span className="text-base font-bold text-purple-900">FREE</span>
                  </div>
                  <p className="text-xs text-purple-700 mt-2">Unlimited Free Access - Included with your account</p>
                </div>
              ) : (
                // No subscription (free_user or a_la_carte) - Full price
                <div className="text-left bg-blue-50 border border-blue-200 rounded p-4 mb-4">
                  <div className="flex justify-between items-center">
                    <span className="text-base font-bold text-blue-900">Service Fee:</span>
                    <span className="text-base font-bold text-blue-900">$15.00</span>
                  </div>
                  <p className="text-xs text-blue-700 mt-2">Standard Service - $15.00</p>
                </div>
              )}
              
              <div className="text-left bg-yellow-50 border border-yellow-200 rounded p-3 mb-4">
                <p className="text-xs text-yellow-800">
                  <strong>⚠️ Important:</strong> Analysis charges are non-refundable. Please verify your manuscript details before proceeding.
                </p>
              </div>
              
              <div className="text-left bg-slate-50 border border-slate-200 rounded p-3 mb-4">
                <p className="text-sm text-slate-800"><strong>Title:</strong> {formData.title}</p>
                <p className="text-sm text-slate-800"><strong>Author:</strong> {formData.author_name}</p>
                <p className="text-sm text-slate-800"><strong>Genre:</strong> {genres.find(g => g.value === formData.genre)?.label}</p>
                {formData.sub_genre && (
                  <p className="text-sm text-slate-800"><strong>Sub-Genre:</strong> {formData.sub_genre}</p>
                )}
                <p className="text-sm text-slate-800"><strong>File:</strong> {selectedFile?.name}</p>
              </div>
              
              <p className="text-sm text-slate-600 mb-6">
                Ready to analyze your manuscript? Click confirm to begin analysis.
              </p>
              
              <div className="flex space-x-3">
                <Button 
                  onClick={handleConfirmAnalysis}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  {user.role === "subscriber" ? "Confirm - Charge $7.50" : 
                   user.role === "a_la_carte" ? "Confirm - Charge $15.00" : 
                   "Confirm - Start Analysis"}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleCancelAnalysis}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && reportToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="text-center">
              <div className="text-4xl mb-4">🗑️</div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">
                Delete Report?
              </h3>
              
              <div className="text-left bg-red-50 border border-red-200 rounded p-4 mb-4">
                <p className="text-sm text-red-800 mb-2">
                  <strong>Warning:</strong> This action cannot be undone.
                </p>
                <p className="text-sm text-red-700">
                  You are about to permanently delete the report for:
                </p>
                <p className="text-sm font-semibold text-red-900 mt-2">
                  "{reportToDelete.title}"
                </p>
              </div>
              
              <p className="text-sm text-slate-600 mb-6">
                Are you absolutely sure you want to delete this ERMT report? 
                This will remove all analysis data and cannot be recovered.
              </p>
              
              <div className="flex space-x-3">
                <Button 
                  onClick={confirmDeleteReport}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                >
                  Yes, Delete Report
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowDeleteModal(false);
                    setReportToDelete(null);
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png" 
                alt="Digital Ink AI" 
                className="h-8 w-auto"
              />
            </div>
            <p className="text-slate-400">
              © 2025 Use What You Got Publishing, LLC. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
      
      {/* Profile Upgrade Modal */}
      <ProfileUpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onUpgrade={handleProfileUpgrade}
        serviceRequested="ERMT Analysis"
      />
      
      {/* Service Charge Confirmation Modal */}
      <ServiceChargeModal
        isOpen={showChargeModal}
        onClose={() => setShowChargeModal(false)}
        onConfirm={handleServiceChargeAuthorization}
        serviceInfo={chargeInfo}
        userType={user?.role || 'a_la_carte'}
      />

      {/* Processing Modal */}
      <ProcessingModal 
        isOpen={isProcessing} 
        serviceName="EPUB-Ready Manuscript Transformer (ERMT)"
        bookTitle={formData.title}
        progress={processingProgress}
        currentStep={currentStep}
        progressSteps={progressSteps}
      />

      {/* API Retry Modal */}
      <APIRetryModal
        isOpen={showRetryModal}
        attemptNumber={retryAttempt}
        maxAttempts={maxRetryAttempts}
        isRetrying={isRetrying}
        errorMessage={retryError}
        onManualRetry={handleManualRetry}
        onCancel={handleCancelRetry}
        serviceName="EPUB-Ready Manuscript Transformer (ERMT)"
      />

      {/* Email Receipt Modal */}
      <EmailReceiptModal
        isOpen={showEmailReceiptModal}
        onClose={handleCloseEmailReceiptModal}
        onSendReceipt={handleSendReceipt}
        serviceName="EPUB-Ready Manuscript Transformer (ERMT)"
        amount={lastCharge?.amount || 15.00}
        bookTitle={lastCharge?.bookTitle || "EPUB Conversion"}
      />
      
      {/* Payment Validation Modal */}
      <PaymentValidationModal
        isOpen={showPaymentValidation}
        onClose={() => {
          setShowPaymentValidation(false);
          navigate('/homepage');
        }}
        onSuccess={handlePaymentValidationSuccess}
        serviceName="EPUB-Ready Manuscript Transformer (ERMT)"
      />
    </div>
  );
};

export default ERMTPage;